import { createRootRoute, createRoute, createRouter } from '@tanstack/react-router';
import { ErrorPage } from './components/ErrorPage';
import { RootLayout } from './components/RootLayout';
import { HomePage } from './pages/HomePage';
import { PackagePage } from './pages/PackagePage';
import { AdvisoryPage } from './pages/AdvisoryPage';
import { AuditPage } from './pages/AuditPage';

/**
 * Code-based route tree (no file-based codegen step). Add a page by creating a
 * component and registering a `createRoute` here, then adding it to
 * `addChildren`.
 */
const rootRoute = createRootRoute({ component: RootLayout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: HomePage,
});

/**
 * Package route. The `name` param is stored as a single URI-encoded segment
 * (e.g. `%40scope%2Fname` for `@scope/name`). The component decodes it with
 * `decodeURIComponent` before display and API calls, so slashes in npm scopes,
 * Go module paths, etc. are round-tripped correctly without requiring splat
 * params or nested router segments.
 */
const packageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pkg/$system/$name',
  component: PackagePage,
  validateSearch: (search: Record<string, unknown>) => ({
    v: typeof search['v'] === 'string' ? search['v'] : undefined,
  }),
});

/** Advisory route — `$id` is a CVE or GHSA id (validated server-side). */
const advisoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/advisory/$id',
  component: AdvisoryPage,
});

/** Manifest audit route — paste or upload a manifest for bulk vulnerability scanning. */
const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/audit',
  component: AuditPage,
});

const routeTree = rootRoute.addChildren([indexRoute, packageRoute, advisoryRoute, auditRoute]);

export const router = createRouter({
  routeTree,
  // Any uncaught error in a route renders the full-screen ErrorPage boundary.
  defaultErrorComponent: ErrorPage,
});

// Register the router instance for full type inference on Links/navigation.
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
